// Resources
import React, { Component } from "react";
import Moment from "react-moment";
import "moment-timezone";
import Switch from "react-switch"; // https://reactjsexample.com/draggable-toggle-switch-component-for-react/
import { field_data } from "../../helpers/getData";
import upsellitLogoUrl from "../../images/Upsellit-Logo-500w.png";
import upsellitMarkUrl from "../../images/logo.svg";
import geologicaRegularUrl from "../../fonts/Geologica-Regular.ttf";
import geologicaBoldUrl from "../../fonts/Geologica-Bold.ttf";

// Styles
import { FormWrapperEl } from "./styles/FormWrapper";

// Condensed "Campaign Details" PDF only renders these sections, in this order.
const CSD_PDF_SECTIONS = ["Strategy", "Desktop Launch", "Mobile Launch", "Engagement"];

// Lockup composited at export time: gradient mark from logo.svg + wordmark cropped from the flat PNG.
const LOGO_WIDTH = 501;
const LOGO_HEIGHT = 113;
const LOGO_WORDMARK_X = 135;

// Longest edge a preview is re-encoded to, to keep the exported PDF a sane size.
const MAX_PREVIEW_EDGE = 1000;

// Geologica is the app's brand face; PDFs fall back to Helvetica if the TTFs can't load.
const PDF_FONT = "Geologica";
const PDF_FONT_FALLBACK = "helvetica";

// Slide backdrop, sampled off the deck artwork: magenta to blue across the top, washing out
// to near-white in the bottom-left so the logo sits on a pale mauve rather than hot pink.
const BACKDROP_TOP = [
	[0.0, [181, 73, 155]],
	[0.3, [174, 76, 158]],
	[0.45, [156, 91, 172]],
	[0.6, [135, 111, 191]],
	[0.75, [112, 135, 212]],
	[1.0, [87, 164, 239]]
];
const BACKDROP_BOTTOM = [
	[0.0, [255, 255, 255]],
	[0.15, [238, 219, 235]],
	[0.3, [208, 167, 208]],
	[0.45, [186, 142, 195]],
	[0.6, [162, 126, 188]],
	[0.75, [141, 114, 182]],
	[1.0, [118, 98, 173]]
];

const CSD_EXPORT_SECTIONS = [
	"Overview",
	"Strategy",
	"Coupon Analytics",
	"Ad Extension Manager",
	"Desktop Launch",
	"Mobile Launch",
	"Engagement",
	"On-Site Design",
	"Emails",
	"Tracking & Links",
	"Re-engagement Rules",
	"Assets",
	"Internal"
];

class FormWrapper extends Component {
	constructor(props) {
		super(props);
		this.state = {
			toggle_reader: false,
			toggle_highlights: false,
			toggle_show: false,
			toggle_compact: false,
			copyMarkdownText: "CSD to MD",
			exportPdfText: "CSD to PDF",
			exportRulesText: "Export Rules"
		};
	}

	componentDidMount() {
		window.addEventListener("load", this._handleLoad);
		this.setState({
			toggle_reader: this.props.reader,
			toggle_highlights: this.props.highlights,
			toggle_show: this.props.show,
			toggle_compact: this.props.compact
		});
	}

	componentWillUnmount() {
		window.removeEventListener("load", this._handleLoad);
	}

	_handleLoad = () => {
		setTimeout(() => {
			this.props.setHeight("form", this.formWrapper.clientHeight + 125);
		}, 200);
	};

	_toggleChanged = (checked, e, id) => {
		this.setState(
			{
				["toggle_" + id]: !!checked
			},
			function () {
				this.props.handleSidebarToggleChanged(id, checked);
			}
		);
	};

	_getFieldTitle = (item) => {
		const fieldTypeId = item.field_type_id || "";
		const exact = field_data[fieldTypeId];
		const normalizedId = fieldTypeId.replace(/_\d+\+/, "_1+");
		const normalized = field_data[normalizedId];
		return (exact && exact.title) || (normalized && normalized.title) || item.title || fieldTypeId;
	};

	_getFieldValue = (item) => {
		if (item.input_type === "boolean") return item.content ? "YES" : "NO";
		if (!item.content) return "";
		if (typeof item.content !== "string") return String(item.content);
		if (item.content.indexOf('{"blocks"') === 0) {
			try {
				const raw = JSON.parse(item.content);
				if (raw && raw.blocks) {
					return raw.blocks.map((block) => block.text || "").join("\n").trim();
				}
			} catch (e) {
				return item.content;
			}
		}
		return item.content.trim();
	};

	_isExportableField = (item, value) => {
		if (!item || (item.is_hidden && !item.force_show)) return false;
		if (item.input_type === "boolean") return true;
		return !!value;
	};

	_formatFieldMarkdown = (item) => {
		const title = this._getFieldTitle(item);
		const value = this._getFieldValue(item);
		if (!this._isExportableField(item, value)) return "";
		if (value.indexOf("\n") !== -1) return `**${title}:**\n${value}`;
		return `**${title}:** ${value}`;
	};

	_getSectionFieldGroups = (section) => {
		const fields = section.fields.filter(Boolean).sort((a, b) => a.order - b.order);
		const ungrouped = [];
		const groups = [];
		const groupLookup = {};

		fields.forEach((field) => {
			if (!field.group_id) {
				ungrouped.push(field);
				return;
			}
			if (!groupLookup[field.group_id]) {
				groupLookup[field.group_id] = {
					group_title: field.group_title,
					group_num: field.group_num,
					fields: []
				};
				groups.push(groupLookup[field.group_id]);
			}
			groupLookup[field.group_id].fields.push(field);
		});

		return {
			ungrouped,
			groups: groups.sort((a, b) => Number(a.group_num || 0) - Number(b.group_num || 0))
		};
	};

	_renderMarkdownSection = (section) => {
		if (!section || !Array.isArray(section.fields)) return "";
		const { ungrouped, groups } = this._getSectionFieldGroups(section);
		const lines = [`## ${section.title}`];
		ungrouped
			.map((field) => this._formatFieldMarkdown(field))
			.filter(Boolean)
			.forEach((line) => lines.push(line));

		groups.forEach((group) => {
			const groupLines = group.fields.map((field) => this._formatFieldMarkdown(field)).filter(Boolean);
			if (groupLines.length === 0) return;
			lines.push(`### ${group.group_title || "Group " + group.group_num}`);
			lines.push(...groupLines);
		});

		return lines.length > 1 ? lines.join("\n\n") : "";
	};

	_getExportableFieldRow = (field) => {
		const value = this._getFieldValue(field);
		if (!this._isExportableField(field, value)) return null;
		return {
			type: "field",
			label: this._getFieldTitle(field),
			value,
			field
		};
	};

	_getCsdExportSections = () => {
		const fields = (this.props.formState || {}).fields || {};

		return CSD_EXPORT_SECTIONS.map((sectionName) => {
			const section = fields[sectionName];
			if (!section || !Array.isArray(section.fields)) return null;
			const { ungrouped, groups } = this._getSectionFieldGroups(section);
			const rows = [];

			ungrouped
				.map((field) => this._getExportableFieldRow(field))
				.filter(Boolean)
				.forEach((row) => rows.push(row));

			groups.forEach((group) => {
				const groupRows = group.fields.map((field) => this._getExportableFieldRow(field)).filter(Boolean);
				if (groupRows.length === 0) return;
				rows.push({
					type: "group",
					label: group.group_title || "Group " + group.group_num
				});
				rows.push(...groupRows);
			});

			return rows.length > 0
				? {
						title: section.title || sectionName,
						rows
				  }
				: null;
		}).filter(Boolean);
	};

	_getCsdMarkdown = () => {
		const form = this.props.form;
		const formState = this.props.formState || {};
		const fields = formState.fields || {};
		const company = form && form._company ? form._company : {};
		const lines = [`# ${company.title || "Company"} - ${form.title || "CSD"}`];

		if (company.title) lines.push(`**Company:** ${company.title}`);
		if (form.title) lines.push(`**CSD:** ${form.title}`);
		if (form.type) lines.push(`**Type:** ${form.type.toUpperCase()}`);
		if (company.adminId) lines.push(`**Admin ID:** ${company.adminId}`);
		if (company.url) lines.push(`**URL:** ${company.url}`);
		if (this.props.mainVersion) lines.push(`**Version:** ${this.props.mainVersion}`);

		const sectionMarkdown = CSD_EXPORT_SECTIONS
			.map((sectionName) => fields[sectionName] && this._renderMarkdownSection(fields[sectionName]))
			.filter(Boolean);

		return lines.concat(sectionMarkdown).join("\n\n").trim();
	};

	_getCsdPdfFilename = (suffix = "export") => {
		const form = this.props.form || {};
		const company = form._company || {};
		const source = `${company.title || "company"}-${form.title || "csd"}-${suffix}`;
		const slug = source
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "");

		return `${slug || "csd-export"}.pdf`;
	};

	// Asset hosts don't send CORS headers, so remote images go through our own server.
	_getProxiedUrl = (url) =>
		/^https?:\/\//i.test(url) ? `/api/v1/image_proxy?url=${encodeURIComponent(url)}` : url;

	_getImageDataUrl = async (url) => {
		const proxied = this._getProxiedUrl(url);
		// /api routes are behind auth middleware, so the proxy needs the bearer token.
		const token = localStorage.getItem("csd_login_token") || window.csd_login_token;
		const headers = proxied !== url && token ? { Authorization: `Bearer ${token}` } : undefined;
		const response = await fetch(proxied, headers ? { headers } : undefined);
		if (!response.ok) throw new Error(`Image request failed (${response.status})`);
		const blob = await response.blob();

		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	};

	_normalizePreviewUrl = (url) => {
		if (!url) return "";
		if (url.indexOf("dropbox.com") !== -1) {
			return url.replace("dl=0", "dl=1").replace("www.dropbox", "dl.dropbox");
		}
		return url;
	};

	// Pulls mobile/desktop creative previews out of any image link stored on the CSD.
	// Matching is per line, so a "Mobile: <url>" / "Desktop: <url>" list resolves correctly
	// even when both links live in the same field.
	_getDesignPreviewUrls = () => {
		const sections = this._getCsdExportSections();
		const imagePattern = /https?:\/\/[^\s"'<>]+\.(?:png|jpe?g|gif|webp|avif)(?:\?[^\s"'<>]*)?/i;
		const previews = { mobile: "", desktop: "" };
		const extras = [];

		sections.forEach((section) => {
			section.rows.forEach((row) => {
				if (row.type !== "field") return;
				String(row.value || "")
					.split("\n")
					.forEach((line) => {
						const match = line.match(imagePattern);
						if (!match) return;
						const url = this._normalizePreviewUrl(match[0]);
						// A line naming its own platform wins over the field/section label.
						const haystack = /mobile|desktop/i.test(line) ? line : `${section.title} ${row.label}`;
						if (/mobile/i.test(haystack)) {
							if (!previews.mobile) previews.mobile = url;
						} else if (/desktop/i.test(haystack)) {
							if (!previews.desktop) previews.desktop = url;
						} else {
							extras.push(url);
						}
					});
			});
		});

		if (!previews.mobile && extras.length) previews.mobile = extras.shift();
		if (!previews.desktop && extras.length) previews.desktop = extras.shift();

		return previews;
	};

	_loadHtmlImage = (url) =>
		new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => resolve(img);
			img.onerror = reject;
			img.src = url;
		});

	// The shipped PNG has a flat dark mark; swap in the gradient SVG mark (with its corner cutout)
	// and keep the PNG's wordmark so the lockup matches the deck artwork.
	_buildLogoDataUrl = async () => {
		const [mark, logo] = await Promise.all([
			this._loadHtmlImage(upsellitMarkUrl),
			this._loadHtmlImage(upsellitLogoUrl)
		]);
		// 2x the 501pt artboard is ~8x the placed size — plenty for print, and a quarter
		// of the pixels of a 4x raster, which jsPDF would otherwise embed as megabytes.
		const scale = 2;
		const canvas = document.createElement("canvas");
		canvas.width = LOGO_WIDTH * scale;
		canvas.height = LOGO_HEIGHT * scale;

		const ctx = canvas.getContext("2d");
		ctx.scale(scale, scale);

		const markWidth = LOGO_HEIGHT * ((mark.naturalWidth || 1) / (mark.naturalHeight || 1));
		ctx.drawImage(mark, 0, 0, markWidth, LOGO_HEIGHT);
		ctx.drawImage(
			logo,
			LOGO_WORDMARK_X,
			0,
			LOGO_WIDTH - LOGO_WORDMARK_X,
			LOGO_HEIGHT,
			LOGO_WORDMARK_X,
			0,
			LOGO_WIDTH - LOGO_WORDMARK_X,
			LOGO_HEIGHT
		);

		return canvas.toDataURL("image/png");
	};

	// PDF has no WebP/AVIF/GIF support, so every preview is re-encoded as PNG via canvas.
	// Reading from a data URL keeps the canvas untainted.
	_loadPdfImage = async (url) => {
		const image = await this._loadHtmlImage(await this._getImageDataUrl(url));
		const width = image.naturalWidth || 1;
		const height = image.naturalHeight || 1;
		const scale = Math.min(1, MAX_PREVIEW_EDGE / Math.max(width, height));
		const canvas = document.createElement("canvas");
		canvas.width = Math.max(1, Math.round(width * scale));
		canvas.height = Math.max(1, Math.round(height * scale));
		canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);

		return { dataUrl: canvas.toDataURL("image/png"), width, height, format: "PNG" };
	};

	// Rows whose value is already implied by a sibling row, so the condensed export omits them.
	_isExcludedPdfRow = (row) => {
		const id = (row.field && row.field.field_type_id) || "";
		const label = row.label || "";
		if (/^(desktop|mobile)_launch$/.test(id)) return true;
		if (/is_incremental_lift/.test(id)) return true;
		if (/^(desktop|mobile) launch$/i.test(label)) return true;
		if (/^is incremental lift$/i.test(label)) return true;
		return false;
	};

	_getPdfCards = () => {
		const allSections = this._getCsdExportSections();

		return CSD_PDF_SECTIONS.map((sectionName) => {
			const section = allSections.find((item) => item.title === sectionName);
			if (!section) return null;
			const rows = section.rows.filter((row) => row.type === "field" && !this._isExcludedPdfRow(row));
			return rows.length ? { title: sectionName, rows } : null;
		}).filter(Boolean);
	};

	_loadFontBase64 = async (url) => {
		const response = await fetch(url);
		if (!response.ok) throw new Error("Font request failed");
		const bytes = new Uint8Array(await response.arrayBuffer());
		const chunk = 0x8000;
		let binary = "";
		for (let i = 0; i < bytes.length; i += chunk) {
			binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
		}
		return btoa(binary);
	};

	_loadPdfFonts = async () => {
		const [regular, bold] = await Promise.all([
			this._loadFontBase64(geologicaRegularUrl),
			this._loadFontBase64(geologicaBoldUrl)
		]);
		return { regular, bold };
	};

	// Returns the family to draw with — Geologica once embedded, Helvetica if unavailable.
	_registerPdfFonts = (doc, fonts) => {
		if (!fonts || !fonts.regular || !fonts.bold) return PDF_FONT_FALLBACK;
		try {
			doc.addFileToVFS("Geologica-Regular.ttf", fonts.regular);
			doc.addFont("Geologica-Regular.ttf", PDF_FONT, "normal");
			doc.addFileToVFS("Geologica-Bold.ttf", fonts.bold);
			doc.addFont("Geologica-Bold.ttf", PDF_FONT, "bold");
			return PDF_FONT;
		} catch (err) {
			console.error("[ CSD to PDF ] Font registration failed:", err);
			return PDF_FONT_FALLBACK;
		}
	};

	// Shared card engine: measuring, drawing and font-size fitting for both PDF exports.
	_createPdfRenderer = (doc, font = PDF_FONT_FALLBACK, cardGap = 12) => {
		const lineFactor = 1.22;
		const useFont = (style) => doc.setFont(font, style);
		const colors = {
			text: [26, 26, 26],
			muted: [110, 116, 128],
			border: [223, 226, 232],
			panel: [240, 241, 243],
			link: [42, 110, 224],
			pink: [232, 30, 128],
			white: [255, 255, 255]
		};

		doc.setLineHeightFactor(lineFactor);

		const cleanText = (text) =>
			String(text || "")
				.replace(/\r/g, "")
				.replace(/\t/g, "    ")
				.replace(/[\u200B-\u200D\uFEFF]/g, "")
				.trim();

		const splitText = (text, width) => {
			const paragraphs = cleanText(text)
				.split(/\n+/)
				.map((paragraph) => paragraph.trim())
				.filter(Boolean);
			if (paragraphs.length === 0) return [""];
			return paragraphs.reduce((lines, paragraph) => lines.concat(doc.splitTextToSize(paragraph, width)), []);
		};

		const setTextColor = (color) => doc.setTextColor(color[0], color[1], color[2]);
		const setFillColor = (color) => doc.setFillColor(color[0], color[1], color[2]);
		const setDrawColor = (color) => doc.setDrawColor(color[0], color[1], color[2]);

		// Measures a card at a candidate font size so the caller can search for the largest that fits.
		const layoutCard = (card, base, width) => {
			const pad = Math.max(8, base * 0.85);
			const titleSize = base * 1.2;
			const labelSize = base * 0.86;
			const labelWidth = Math.max(64, width * 0.34);
			const valueX = pad + labelWidth + 10;
			const valueWidth = width - valueX - pad;
			const lineHeight = base * lineFactor;
			const labelLineHeight = labelSize * lineFactor;

			const rows = card.rows.map((row) => {
				useFont("bold");
				doc.setFontSize(labelSize);
				const labelLines = doc.splitTextToSize(cleanText(row.label), labelWidth);
				const isLink = /https?:\/\//i.test(row.value);
				useFont("normal");
				doc.setFontSize(isLink ? base * 0.9 : base);
				const valueLines = splitText(row.value, valueWidth);
				const height =
					Math.max(labelLines.length * labelLineHeight, valueLines.length * lineHeight) + base * 0.72;
				return { labelLines, valueLines, height, isLink };
			});

			const height =
				pad +
				titleSize * 1.15 +
				base * 0.35 +
				rows.reduce((total, row) => total + row.height, 0) +
				pad * 0.5;

			return { base, pad, titleSize, labelSize, labelWidth, valueX, rows, height };
		};

		const drawCard = (card, layout, x, y, width) => {
			setFillColor(colors.white);
			setDrawColor(colors.border);
			doc.setLineWidth(0.75);
			doc.roundedRect(x, y, width, layout.height, 8, 8, "FD");

			useFont("bold");
			doc.setFontSize(layout.titleSize);
			setTextColor(colors.text);
			doc.text(cleanText(card.title), x + layout.pad, y + layout.pad + layout.titleSize * 0.85);

			let rowY = y + layout.pad + layout.titleSize * 1.15 + layout.base * 0.35;
			layout.rows.forEach((row, index) => {
				if (index > 0) {
					setDrawColor(colors.border);
					doc.setLineWidth(0.5);
					doc.line(x + layout.pad * 0.5, rowY, x + width - layout.pad * 0.5, rowY);
				}
				const textTop = rowY + layout.base * 0.36;
				useFont("bold");
				doc.setFontSize(layout.labelSize);
				setTextColor(colors.muted);
				doc.text(row.labelLines, x + layout.pad + layout.labelWidth, textTop + layout.labelSize * 0.85, {
					align: "right"
				});
				useFont("normal");
				doc.setFontSize(row.isLink ? layout.base * 0.9 : layout.base);
				setTextColor(row.isLink ? colors.link : colors.text);
				doc.text(row.valueLines, x + layout.valueX, textTop + layout.base * 0.85);
				rowY += row.height;
			});
		};

		// Largest font size at which every card still fits the column.
		const fitColumn = (cardList, width, availableHeight, minBase, maxBase) => {
			for (let base = maxBase; base >= minBase; base -= 0.25) {
				const layouts = cardList.map((card) => layoutCard(card, base, width));
				const total =
					layouts.reduce((sum, layout) => sum + layout.height, 0) + cardGap * (layouts.length - 1);
				if (total <= availableHeight) return layouts;
			}
			return null;
		};

		// Greedy fill used when the full set cannot fit at the minimum readable size.
		const packColumn = (cardList, width, availableHeight, base) => {
			const packed = [];
			let used = 0;
			for (let i = 0; i < cardList.length; i++) {
				const layout = layoutCard(cardList[i], base, width);
				const next = used + layout.height + (packed.length ? cardGap : 0);
				if (next > availableHeight && packed.length) break;
				packed.push({ card: cardList[i], layout });
				used = next;
			}
			return packed;
		};

		const drawColumn = (entries, x, top, width) => {
			let y = top;
			entries.forEach((entry) => {
				drawCard(entry.card, entry.layout, x, y, width);
				y += entry.layout.height + cardGap;
			});
		};

		return {
			cardGap,
			lineFactor,
			colors,
			useFont,
			cleanText,
			splitText,
			setTextColor,
			setFillColor,
			setDrawColor,
			layoutCard,
			fitColumn,
			packColumn,
			drawColumn
		};
	};

	_buildCsdPdf = (PdfDocument, assets = {}) => {
		const form = this.props.form || {};
		const company = form._company || {};
		const logoDataUrl = assets.logo || "";
		const previews = assets.previews || {};
		const cards = this._getPdfCards();

		const pageWidth = 960;
		const pageHeight = 540;
		const doc = new PdfDocument({ orientation: "landscape", unit: "pt", format: [pageWidth, pageHeight] });
		const margin = 34;
		const colGap = 26;
		const contentTop = 30;
		const contentBottom = pageHeight - margin;
		const rightWidth = 348;
		const rightX = pageWidth - margin - rightWidth;
		const leftX = margin;
		const leftWidth = rightX - colGap - leftX;
		const {
			lineFactor,
			colors,
			useFont,
			cleanText,
			setTextColor,
			setFillColor,
			setDrawColor,
			fitColumn,
			packColumn,
			drawColumn
		} = this._createPdfRenderer(doc, this._registerPdfFonts(doc, assets.fonts));

		// Stepped cutout in the bottom-left of the white sheet; the pink plate shows through it.
		const notch = { x: 183, y: 475, rLeft: 40, rStep: 30, rBottom: 30 };
		const inset = 9;
		const sheet = { left: inset, top: inset, right: pageWidth - inset, bottom: pageHeight - inset, radius: 20 };

		// Quarter-arc control points; works for convex and concave corners alike.
		const arcTo = (from, to, center) => {
			const k = 0.5523;
			return {
				x1: from.x + k * (to.x - center.x),
				y1: from.y + k * (to.y - center.y),
				x2: to.x + k * (from.x - center.x),
				y2: to.y + k * (from.y - center.y),
				x: to.x,
				y: to.y
			};
		};

		// jsPDF wants each segment relative to the previous point, so convert from absolute.
		const fillPath = (points, style) => {
			const [start, ...rest] = points;
			let cx = start.x;
			let cy = start.y;
			const segments = rest.map((point) => {
				const segment =
					point.x1 === undefined
						? [point.x - cx, point.y - cy]
						: [
								point.x1 - cx,
								point.y1 - cy,
								point.x2 - cx,
								point.y2 - cy,
								point.x - cx,
								point.y - cy
						  ];
				cx = point.x;
				cy = point.y;
				return segment;
			});
			doc.lines(segments, start.x, start.y, [1, 1], style, true);
		};

		const sheetOutline = () => {
			const { left, top, right, bottom, radius } = sheet;
			const { x: nx, y: ny, rLeft, rStep, rBottom } = notch;

			return [
				{ x: left + radius, y: top },
				{ x: right - radius, y: top },
				arcTo({ x: right - radius, y: top }, { x: right, y: top + radius }, { x: right - radius, y: top + radius }),
				{ x: right, y: bottom - radius },
				arcTo({ x: right, y: bottom - radius }, { x: right - radius, y: bottom }, { x: right - radius, y: bottom - radius }),
				{ x: nx + rBottom, y: bottom },
				arcTo({ x: nx + rBottom, y: bottom }, { x: nx, y: bottom - rBottom }, { x: nx + rBottom, y: bottom - rBottom }),
				{ x: nx, y: ny + rStep },
				arcTo({ x: nx, y: ny + rStep }, { x: nx - rStep, y: ny }, { x: nx - rStep, y: ny + rStep }),
				{ x: left + rLeft, y: ny },
				arcTo({ x: left + rLeft, y: ny }, { x: left, y: ny - rLeft }, { x: left + rLeft, y: ny - rLeft }),
				{ x: left, y: top + radius },
				arcTo({ x: left, y: top + radius }, { x: left + radius, y: top }, { x: left + radius, y: top + radius })
			];
		};

		const sampleStops = (stops, t) => {
			for (let i = 0; i < stops.length - 1; i++) {
				const [start, from] = stops[i];
				const [end, to] = stops[i + 1];
				if (t <= end || i === stops.length - 2) {
					const local = end === start ? 0 : Math.min(1, Math.max(0, (t - start) / (end - start)));
					return from.map((value, channel) => value + (to[channel] - value) * local);
				}
			}
			return stops[stops.length - 1][1];
		};

		// Two-axis wash: horizontal stops for the top and bottom edges, blended vertically.
		// The vertical curve is steep on the left (colour holds, then drops to white near the
		// bottom) and linear on the right, which is what the artwork does.
		const drawBackdrop = () => {
			// Fine cells keep the wash smooth; the white sheet hides most of the page, so
			// interior cells are skipped rather than drawn and covered.
			const cell = 3;
			const interior = {
				left: sheet.left + sheet.radius,
				top: sheet.top + sheet.radius,
				right: sheet.right - sheet.radius,
				bottom: sheet.bottom - sheet.radius
			};
			const notchGuardX = notch.x + notch.rBottom + 2;
			const notchGuardY = notch.y - notch.rLeft - 2;

			for (let x = 0; x < pageWidth; x += cell) {
				const xt = Math.min(1, x / pageWidth);
				const top = sampleStops(BACKDROP_TOP, xt);
				const bottom = sampleStops(BACKDROP_BOTTOM, xt);
				const power = 2.2 + (1 - 2.2) * xt;

				for (let y = 0; y < pageHeight; y += cell) {
					const coveredBySheet =
						x >= interior.left &&
						x + cell <= interior.right &&
						y >= interior.top &&
						y + cell <= interior.bottom &&
						(x >= notchGuardX || y + cell <= notchGuardY);
					if (coveredBySheet) continue;

					const vt = Math.pow(Math.min(1, y / pageHeight), power);
					const color = top.map((value, channel) => Math.round(value + (bottom[channel] - value) * vt));
					doc.setFillColor(color[0], color[1], color[2]);
					doc.rect(x, y, cell + 0.6, cell + 0.6, "F");
				}
			}
		};

		const drawFrame = () => {
			drawBackdrop();
			setFillColor(colors.white);
			fillPath(sheetOutline(), "F");
		};

		const drawLogo = (x, y, width) => {
			const height = width * (LOGO_HEIGHT / LOGO_WIDTH);
			if (logoDataUrl) {
				try {
					// jsPDF defaults to no compression, which embeds this as multiple megabytes.
					doc.addImage(logoDataUrl, "PNG", x, y, width, height, "usi-logo", "FAST");
					return;
				} catch (err) {
					console.error("[ CSD to PDF ] Logo render failed:", err);
				}
			}
			useFont("bold");
			doc.setFontSize(20);
			setTextColor(colors.text);
			doc.text("upsellit", x, y + height - 4);
		};

		const headingText = cleanText(form.title);

		const drawPreviewBox = (x, top, width, height, label, image) => {
			if (image) {
				setDrawColor(colors.border);
				doc.setLineWidth(0.75);
				doc.rect(x, top, width, height, "S");
				const scale = Math.min((width - 20) / image.width, (height - 20) / image.height);
				const drawWidth = image.width * scale;
				const drawHeight = image.height * scale;
				try {
					doc.addImage(
						image.dataUrl,
						image.format,
						x + (width - drawWidth) / 2,
						top + (height - drawHeight) / 2,
						drawWidth,
						drawHeight,
						`preview-${label}`,
						"MEDIUM"
					);
				} catch (err) {
					console.error("[ CSD to PDF ] Preview render failed:", err);
				}
			} else {
				// No creative on the CSD: leave a plain grey plate rather than an error message.
				setFillColor(colors.panel);
				doc.rect(x, top, width, height, "F");
			}

			useFont("bold");
			doc.setFontSize(17);
			setTextColor(colors.muted);
			doc.text(label, x + width / 2, top + height + 20, { align: "center" });
		};

		const drawLeftPanel = () => {
			const titleSize = 33;
			const chipHeight = 50;
			useFont("bold");
			doc.setFontSize(titleSize);
			const titleText = "Campaign Details";
			const chipWidth = Math.min(leftWidth, doc.getTextWidth(titleText) + 38);
			setFillColor(colors.panel);
			doc.roundedRect(leftX, contentTop, chipWidth, chipHeight, 10, 10, "F");
			setTextColor(colors.text);
			doc.text(titleText, leftX + 19, contentTop + 36);

			let y = contentTop + chipHeight + 28;
			if (headingText) {
				const headingSize = 19;
				useFont("bold");
				doc.setFontSize(headingSize);
				setTextColor(colors.pink);
				const lines = doc.splitTextToSize(headingText, leftWidth).slice(0, 3);
				doc.text(lines, leftX, y);
				y += lines.length * headingSize * lineFactor + 16;
			}

			const logoWidth = 126;
			const logoHeight = logoWidth * (LOGO_HEIGHT / LOGO_WIDTH);
			const captionHeight = 28;
			const boxTop = y;
			// Mockups and captions stay clear of the cutout; the logo sits centred inside it.
			const boxHeight = notch.y - 14 - captionHeight - boxTop;

			if (boxHeight > 60) {
				const gap = 20;
				const mobileWidth = (leftWidth - gap) * 0.34;
				const desktopWidth = leftWidth - gap - mobileWidth;
				drawPreviewBox(leftX, boxTop, mobileWidth, boxHeight, "Mobile Design", previews.mobile);
				drawPreviewBox(
					leftX + mobileWidth + gap,
					boxTop,
					desktopWidth,
					boxHeight,
					"Desktop Design",
					previews.desktop
				);
			}

			drawLogo(leftX, notch.y + (sheet.bottom - notch.y - logoHeight) / 2, logoWidth);
		};

		drawFrame();
		drawLeftPanel();

		const availableHeight = contentBottom - contentTop;
		const fitted = fitColumn(cards, rightWidth, availableHeight, 6.5, 15);
		let remaining = [];

		if (fitted) {
			drawColumn(
				cards.map((card, index) => ({ card, layout: fitted[index] })),
				rightX,
				contentTop,
				rightWidth
			);
		} else {
			const packed = packColumn(cards, rightWidth, availableHeight, 6.5);
			drawColumn(packed, rightX, contentTop, rightWidth);
			remaining = cards.slice(packed.length);
		}

		// Anything that could not fit continues on full-width two-column pages.
		while (remaining.length) {
			doc.addPage([pageWidth, pageHeight], "landscape");
			drawFrame();
			useFont("bold");
			doc.setFontSize(26);
			setTextColor(colors.text);
			doc.text("Campaign Details (cont.)", margin, contentTop + 32);

			const columnWidth = (pageWidth - margin * 2 - colGap) / 2;
			const columnTop = contentTop + 58;
			const columnHeight = contentBottom - columnTop;
			let placed = 0;

			for (let col = 0; col < 2 && placed < remaining.length; col++) {
				const packed = packColumn(remaining.slice(placed), columnWidth, columnHeight, 11);
				drawColumn(packed, margin + col * (columnWidth + colGap), columnTop, columnWidth);
				placed += packed.length;
			}

			remaining = remaining.slice(placed);
		}

		return {
			doc,
			filename: this._getCsdPdfFilename()
		};
	};

	// Rules only: same card styling on a bare page, with no frame, title, mockups or logo.
	_buildRulesPdf = (PdfDocument, assets = {}) => {
		const cards = this._getPdfCards();
		const pageWidth = 612;
		const pageHeight = 792;
		const doc = new PdfDocument({ orientation: "portrait", unit: "pt", format: [pageWidth, pageHeight] });
		const margin = 32;
		const columnWidth = pageWidth - margin * 2;
		const columnHeight = pageHeight - margin * 2;
		const { fitColumn, packColumn, drawColumn } = this._createPdfRenderer(
			doc,
			this._registerPdfFonts(doc, assets.fonts)
		);

		// Single column down the sheet, typeset as large as the page allows.
		const fitted = fitColumn(cards, columnWidth, columnHeight, 9, 34);

		if (fitted) {
			drawColumn(
				cards.map((card, index) => ({ card, layout: fitted[index] })),
				margin,
				margin,
				columnWidth
			);
			return { doc, filename: this._getCsdPdfFilename("rules") };
		}

		let remaining = cards;
		let firstPage = true;

		while (remaining.length) {
			if (!firstPage) doc.addPage([pageWidth, pageHeight], "portrait");
			firstPage = false;

			const packed = packColumn(remaining, columnWidth, columnHeight, 9);
			drawColumn(packed, margin, margin, columnWidth);
			remaining = remaining.slice(packed.length);
		}

		return { doc, filename: this._getCsdPdfFilename("rules") };
	};

	_downloadPdf = (doc, filename, previewWindow) => {
		const blob = doc.output("blob");
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");

		if (previewWindow) previewWindow.location.href = url;
		else window.open(url, "_blank");

		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setTimeout(() => URL.revokeObjectURL(url), 60000);
	};

	_runPdfExport = async (stateKey, idleLabel, build) => {
		this.setState({ [stateKey]: "Building PDF" });
		const previewWindow = window.open("", "_blank");

		try {
			const { jsPDF } = await import("jspdf");
			const { doc, filename } = await build(jsPDF);
			this._downloadPdf(doc, filename, previewWindow);
			this.setState({ [stateKey]: "Downloaded" });
		} catch (err) {
			if (previewWindow) previewWindow.close();
			console.error(`[ ${idleLabel} ] Export failed:`, err);
			this.setState({ [stateKey]: "Export Failed" });
		}

		setTimeout(() => this.setState({ [stateKey]: idleLabel }), 1500);
	};

	_exportCsdPdf = () =>
		this._runPdfExport("exportPdfText", "CSD to PDF", async (jsPDF) => {
			const previewUrls = this._getDesignPreviewUrls();
			const loadPreview = (url) =>
				url
					? this._loadPdfImage(url).catch((err) => {
							console.error("[ CSD to PDF ] Preview load failed:", err);
							return null;
					  })
					: Promise.resolve(null);
			const [logoDataUrl, fonts, mobile, desktop] = await Promise.all([
				this._buildLogoDataUrl().catch((err) => {
					console.error("[ CSD to PDF ] Logo load failed:", err);
					return "";
				}),
				this._loadPdfFonts().catch((err) => {
					console.error("[ CSD to PDF ] Font load failed:", err);
					return null;
				}),
				loadPreview(previewUrls.mobile),
				loadPreview(previewUrls.desktop)
			]);

			return this._buildCsdPdf(jsPDF, { logo: logoDataUrl, fonts, previews: { mobile, desktop } });
		});

	_exportRulesPdf = () =>
		this._runPdfExport("exportRulesText", "Export Rules", async (jsPDF) => {
			const fonts = await this._loadPdfFonts().catch((err) => {
				console.error("[ Export Rules ] Font load failed:", err);
				return null;
			});

			return this._buildRulesPdf(jsPDF, { fonts });
		});

	_copyCsdMarkdown = () => {
		const markdown = this._getCsdMarkdown();
		const onSuccess = () => {
			this.setState({ copyMarkdownText: "Copied" });
			setTimeout(() => this.setState({ copyMarkdownText: "CSD to MD" }), 1500);
		};
		const onError = (err) => {
			console.error("[ CSD to MD ] Copy failed:", err);
			this.setState({ copyMarkdownText: "Copy Failed" });
			setTimeout(() => this.setState({ copyMarkdownText: "CSD to MD" }), 1500);
		};

		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(markdown).then(onSuccess).catch(onError);
			return;
		}

		try {
			const el = document.createElement("textarea");
			el.value = markdown;
			el.setAttribute("readonly", "");
			el.style.position = "fixed";
			el.style.left = "-9999px";
			document.body.appendChild(el);
			el.select();
			document.execCommand("copy");
			document.body.removeChild(el);
			onSuccess();
		} catch (err) {
			onError(err);
		}
	};

	renderToggle(id, label, label2) {
		return (
			<div className={"toggle-switch-wrapper"}>
				<label htmlFor="normal-switch">
					<Switch
						onChange={this._toggleChanged}
						checked={!!this.state["toggle_" + id]}
						id={id}
						height={20}
						width={40}
						className="toggle-switch"
						onColor="#1f85de"
						offColor="#9eb2c6"
					/>
					<p className="toggle-label">{!!this.state["toggle_" + id] ? label : label2}</p>
				</label>
			</div>
		);
	}

	render() {
		Moment.globalTimezone = Intl.DateTimeFormat()["resolvedOptions"]()["timeZone"];
		return (
			<React.Fragment>
				<FormWrapperEl ref={(ref) => (this.formWrapper = ref)} style={{ flexShrink: 0 }}>
					<div className="info-wrapper">
						{this.props.form && this.props.form._company && (
							<div className="company-wrapper">
								{typeof this.state.toggle_reader !== "undefined" &&
									typeof this.state.toggle_show !== "undefined" && (
										<div className={"company-toggle-wrapper"}>
											{this.renderToggle("reader", "Reader Mode", "Edit Mode")}
											{this.renderToggle("highlights", "Highlights Enabled", "Highlights Disabled")}
											{this.renderToggle("compact", "Compact", "Expanded")}
											<button
												className="csd-markdown-btn"
												type="button"
												onClick={this._copyCsdMarkdown}
												disabled={!this.props.formState || !this.props.formState.fields}
											>
												{this.state.copyMarkdownText}
											</button>
											<button
												className="csd-pdf-btn"
												type="button"
												onClick={this._exportCsdPdf}
												disabled={!this.props.formState || !this.props.formState.fields}
											>
												{this.state.exportPdfText}
											</button>
											<button
												className="csd-pdf-btn"
												type="button"
												onClick={this._exportRulesPdf}
												disabled={!this.props.formState || !this.props.formState.fields}
											>
												{this.state.exportRulesText}
											</button>
											{/* {this.renderToggle("show", "All Fields", "Suggested Fields")} */}
										</div>
									)}
							</div>
						)}
					</div>
				</FormWrapperEl>
			</React.Fragment>
		);
	}
}

export default FormWrapper;
